import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import logoMark from "@/assets/coastside-mark.png";
import { FIRM } from "@/lib/firm";
import { cn } from "@/lib/utils";

const SERVICE_GROUPS = [
  {
    heading: "Property & Conveyancing",
    items: [
      { to: "/conveyancing", label: "Conveyancing" },
      { to: "/commercial-conveyancing", label: "Commercial conveyancing" },
      { to: "/off-the-plan-contracts", label: "Off the plan contracts" },
      { to: "/seller-disclosure", label: "Seller disclosure (Form 2)" },
      { to: "/aml-real-estate-agents", label: "AML for real estate agents" },
    ],
  },
  {
    heading: "Wills, Estates & Elder Law",
    items: [
      { to: "/wills-and-estates", label: "Wills & estates" },
      { to: "/testamentary-trusts", label: "Testamentary trust wills" },
      { to: "/probate", label: "Probate & estate administration" },
      { to: "/cost-to-contest-a-will", label: "Contesting an estate" },
      { to: "/elder-law", label: "Elder law" },
    ],
  },
  {
    heading: "Business & Commercial",
    items: [
      { to: "/business-lawyers", label: "Business law" },
      { to: "/business-sales", label: "Buying & selling a business" },
      { to: "/retail-shop-leases", label: "Retail & commercial leases" },
      { to: "/independent-legal-advice", label: "Independent legal advice" },
    ],
  },
] as const;

const NAV = [
  { to: "/fees", label: "Fees" },
  { to: "/guides", label: "Guides" },
  
  { to: "/our-people", label: "Our People" },
  { to: "/contact", label: "Contact" },
] as const;

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [adVisit, setAdVisit] = useState(false);

  // Visitors arriving from a Google ad on a conveyancing landing page get a
  // distraction-free header (logo + phone) so they stay focused on the quote.
  useEffect(() => {
    try {
      if (/[?&](gclid|gbraid|wbraid)=/.test(window.location.search)) sessionStorage.setItem("ad_visit", "1");
      setAdVisit(sessionStorage.getItem("ad_visit") === "1");
    } catch {
      /* storage unavailable */
    }
  }, [pathname]);
  const focusMode =
    adVisit && (pathname.startsWith("/conveyancing/") || pathname === "/conveyancing-sunshine-coast");

  useEffect(() => {
    if (!servicesOpen) return;
    function onDocClick(event: MouseEvent) {
      if (!servicesRef.current?.contains(event.target as Node)) setServicesOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setServicesOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [servicesOpen]);

  if (focusMode) {
    return (
      <header className="sticky top-0 z-50 border-b border-border/70 bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3">
          <Link to="/" className="brand-wordmark whitespace-nowrap text-lg sm:text-2xl">
            {FIRM.name}
          </Link>
          <a
            href={FIRM.phoneHref}
            className="inline-flex items-center gap-2 rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            <Phone className="size-4" aria-hidden="true" />
            <span className="hidden sm:inline">{FIRM.phone}</span>
            <span className="sm:hidden">Call</span>
          </a>
        </div>
      </header>
    );
  }

  const overlay = pathname === "/" && !open;
  const navText = overlay
    ? "text-primary-foreground/90 hover:text-primary-foreground"
    : "text-muted-foreground hover:text-foreground";

  return (
    <header
      className={cn(
        "top-0 z-50",
        overlay
          ? "absolute inset-x-0 bg-transparent"
          : "sticky border-b border-border/70 bg-background/95 backdrop-blur",
      )}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4">
        <Link to="/" className="flex shrink-0 items-center gap-3 leading-none" onClick={() => setOpen(false)}>
          <img
            src={logoMark}
            alt=""
            width={48}
            height={48}
            className={cn("size-9 object-contain sm:size-12", overlay && "brightness-0 invert")}
          />
          <span
            className={cn(
              "brand-wordmark whitespace-nowrap text-lg sm:text-2xl lg:text-[1.6rem]",
              overlay && "!text-primary-foreground",
            )}
          >
            {FIRM.name}
          </span>


        </Link>


        <nav className="hidden items-center gap-6 lg:flex">
          <div
            ref={servicesRef}
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              type="button"
              onClick={() => setServicesOpen((v) => !v)}
              aria-expanded={servicesOpen}
              aria-haspopup="true"
              className={cn("inline-flex items-center gap-1 text-sm transition-colors", navText)}
            >
              Our Services
              <ChevronDown
                className={cn("size-4 transition-transform", servicesOpen && "rotate-180")}
                aria-hidden="true"
              />
            </button>

            <div
              className={cn(
                "absolute left-1/2 top-full w-[46rem] -translate-x-1/2 pt-3",
                servicesOpen ? "block" : "hidden",
              )}
            >
              <div className="grid grid-cols-3 gap-6 rounded-sm border border-border bg-card p-6 shadow-lg">
                {SERVICE_GROUPS.map((group) => (
                  <div key={group.heading}>
                    <p className="text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      {group.heading}
                    </p>
                    <ul className="mt-3 space-y-2">
                      {group.items.map((item) => (
                        <li key={item.to}>
                          <Link
                            to={item.to}
                            onClick={() => setServicesOpen(false)}
                            className="block text-sm leading-snug text-foreground/85 transition-colors hover:text-primary"
                            activeProps={{ className: "text-primary font-semibold" }}
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={cn("text-sm transition-colors", navText)}
              activeProps={{ className: "font-semibold" }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {overlay && (
            <Link
              to="/contact"
              className="hidden rounded-md bg-card px-4 py-2 text-sm font-semibold text-foreground shadow-sm transition-opacity hover:opacity-90 xl:inline-flex"
            >
              Get a fixed quote
            </Link>
          )}
          <a
            href={FIRM.phoneHref}
            className={cn(
              "hidden items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 sm:inline-flex",
              overlay
                ? "border border-primary-foreground/80 text-primary-foreground"
                : "bg-primary text-primary-foreground",
            )}
          >
            <Phone className="size-4" aria-hidden="true" />
            {FIRM.phone}
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className={cn(
              "inline-flex size-10 items-center justify-center rounded-sm border lg:hidden",
              overlay ? "border-primary-foreground/60 text-primary-foreground" : "border-border text-foreground",
            )}
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      <div className={cn("border-t border-border bg-card lg:hidden", open ? "block" : "hidden")}>
        <nav className="mx-auto flex max-w-6xl flex-col px-5 py-2">
          {SERVICE_GROUPS.map((group) => (
            <div key={group.heading} className="border-b border-border/60 py-3">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                {group.heading}
              </p>
              <ul className="mt-2 space-y-2">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className="block text-sm text-foreground"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className="border-b border-border/60 py-3 text-sm text-foreground last:border-0"
            >
              {item.label}
            </Link>
          ))}
          <a
            href={FIRM.phoneHref}
            className="mt-3 mb-3 inline-flex items-center justify-center gap-2 rounded-sm bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground"
          >
            <Phone className="size-4" aria-hidden="true" /> Call {FIRM.phone}
          </a>
        </nav>
      </div>
    </header>
  );
}
